import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { RootState } from "src/store";
import { debounce } from "ts-debounce";
import { useApplicationsService } from "../services";
import { ApplicationFormData, ApplicationSettingsFormData } from "../types";
import { useCreateAdminApplicationFormDrawerController } from "./CreateAdminApplicationFormDrawerController";

export const useAdminApplicationsViewController = () => {
  const {
    loading: applicationsLoading,
    filter: applicationsFilter,
    list: applicationList,
    getApplications,
    changeApplicationsFilter,
    resetApplications,
  } = useApplicationsService();

  const createAdminApplicationFormDrawerController = useCreateAdminApplicationFormDrawerController();

  const initLoad = useCallback(async () => {
    try {
      await getApplications();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadApplications = useCallback(async () => {
    try {
      await getApplications();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadApplicationsDebounce = useMemo(() => debounce(reloadApplications, 500), []);

  const changeApplicationFilterForm = useCallback(async (filter: Partial<RootState["applications"]["filter"]>) => {
    changeApplicationsFilter(filter);
    await reloadApplications();
  }, []);

  const changeApplicationsSearchFilter = useCallback(async (_: ChangeEvent<HTMLInputElement>, query: string) => {
    changeApplicationsFilter({ query });
    await reloadApplicationsDebounce();
  }, []);

  const changeApplicationsSorting = useCallback(async (s: GridSortModel) => {
    changeApplicationsFilter({ sort: `${s[0].field}_${s[0].sort}` });
    await reloadApplications();
  }, []);

  const changeApplicationsPagination = useCallback(async (p: GridPaginationModel) => {
    changeApplicationsFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadApplications();
  }, []);

  useEffect(() => {
    initLoad();

    return () => {
      resetApplications();
    };
  }, []);

  return {
    isPageLoading: applicationsLoading.getList,
    applicationsFilter,
    applicationList,
    changeApplicationFilterForm,
    changeApplicationsSearchFilter,
    changeApplicationsSorting,
    changeApplicationsPagination,
    createAdminApplicationFormDrawerController: {
      ...createAdminApplicationFormDrawerController,
      submit: (
        full: ApplicationFormData<ApplicationSettingsFormData>,
        dirty: Partial<ApplicationFormData<ApplicationSettingsFormData>>
      ) => createAdminApplicationFormDrawerController.submit(full, dirty, getApplications),
    },
  };
};
